/**
 * Health Integration Manager
 * Coordinates health data watching and notification scheduling
 */

import { HealthDataWatcher, WatcherConfig } from './HealthDataWatcher';
import { NotificationScheduler, NotificationSchedule } from './NotificationScheduler';
import { HealthService, ChallengeService, LeaderboardService, FirebaseService } from '../index';
import { ApiResponse, User } from '../types';

export interface HealthIntegrationConfig {
    watcher: Partial<WatcherConfig>;
    notifications: Partial<NotificationSchedule>;
    autoStart: boolean;
    syncInterval: number; // minutes
}

export interface IntegrationStatus {
    watcherActive: boolean;
    notificationsActive: boolean;
    lastHealthSync: Date | null;
    lastNotificationSent: Date | null;
    activeChallenges: number;
    errors: string[];
}

export class HealthIntegrationManager {
    private healthDataWatcher: HealthDataWatcher;
    private notificationScheduler: NotificationScheduler;
    private healthService: HealthService;
    private challengeService: ChallengeService;
    private leaderboardService: LeaderboardService;
    private firebaseService: FirebaseService;
    private config: HealthIntegrationConfig;
    private currentUser: User | null = null;
    private _isInitialized = false;

    constructor(
        healthService: HealthService,
        challengeService: ChallengeService,
        leaderboardService: LeaderboardService,
        firebaseService: FirebaseService,
        config: Partial<HealthIntegrationConfig> = {},

    ) {
        this.healthService = healthService;
        this.challengeService = challengeService;
        this.leaderboardService = leaderboardService;
        this.firebaseService = firebaseService;

        this.config = {
            autoStart: true,
            syncInterval: 15,
            watcher: {
                enabled: true,
                syncInterval: 15,
                autoUpdateChallenges: true,
                notificationReminders: true,
                reminderFrequency: 'twice_daily',
                reminderTimes: ['09:00', '18:00']
            },
            notifications: {
                enabled: true,
                frequency: 'twice_daily',
                times: ['09:00', '18:00'],
                challengeReminders: true,
                leaderboardUpdates: true,
                achievementCelebrations: true,
                dailyProgress: true,
                quietHours: {
                    enabled: false,
                    start: '22:00',
                    end: '08:00'
                }
            },
            ...config
        };

        // Initialize services
        this.healthDataWatcher = new HealthDataWatcher(
            healthService,
            challengeService,
            firebaseService,
            this.config.watcher
        );

        this.notificationScheduler = new NotificationScheduler(
            challengeService,
            leaderboardService,
            firebaseService,
            this.config.notifications
        );
    }

    /**
     * Initialize the health integration manager
     */
    async initialize(user: User): Promise<ApiResponse<void>> {
        try {
            if (this._isInitialized) {
                return { success: true };
            }

            console.log('🔧 HealthIntegrationManager: Initializing...');
            this.currentUser = user;

            // Initialize health service
            const healthInit = await this.healthService.initialize();
            if (!healthInit.success) {
                throw new Error(`Failed to initialize health service: ${healthInit.error}`);
            }

            // Start services if auto-start is enabled
            if (this.config.autoStart) {
                await this.startServices();
            }

            this._isInitialized = true;
            console.log('✅ HealthIntegrationManager: Initialized successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthIntegrationManager: Failed to initialize:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Start all health integration services
     */
    async startServices(): Promise<ApiResponse<void>> {
        try {
            console.log('🔧 HealthIntegrationManager: Starting services...');

            // Start health data watcher
            const watcherResult = await this.healthDataWatcher.startWatching();
            if (!watcherResult.success) {
                throw new Error(`Failed to start health data watcher: ${watcherResult.error}`);
            }

            // Start notification scheduler
            const notificationResult = await this.notificationScheduler.start();
            if (!notificationResult.success) {
                throw new Error(`Failed to start notification scheduler: ${notificationResult.error}`);
            }

            console.log('✅ HealthIntegrationManager: All services started successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthIntegrationManager: Failed to start services:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Stop all health integration services
     */
    async stopServices(): Promise<ApiResponse<void>> {
        try {
            console.log('🔧 HealthIntegrationManager: Stopping services...');

            // Stop health data watcher
            const watcherResult = await this.healthDataWatcher.stopWatching();
            if (!watcherResult.success) {
                console.warn('⚠️ HealthIntegrationManager: Failed to stop health data watcher:', watcherResult.error);
            }

            // Stop notification scheduler
            const notificationResult = await this.notificationScheduler.stop();
            if (!notificationResult.success) {
                console.warn('⚠️ HealthIntegrationManager: Failed to stop notification scheduler:', notificationResult.error);
            }

            console.log('✅ HealthIntegrationManager: All services stopped successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthIntegrationManager: Failed to stop services:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Process health data change from platform-specific code
     */
    async processHealthDataChange(
        type: 'steps' | 'distance' | 'calories' | 'activeMinutes',
        value: number
    ): Promise<ApiResponse<void>> {
        try {
            if (!this.currentUser) {
                throw new Error('No user is currently set');
            }

            const change = {
                type,
                value,
                timestamp: new Date(),
                userId: this.currentUser.id
            };

            // Process through health data watcher
            const result = await this.healthDataWatcher.processHealthDataChange(change);

            if (result.success) {
                // Check if we should send a leaderboard update notification
                await this.checkAndSendLeaderboardUpdate(type, value);
            }

            return result;

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthIntegrationManager: Failed to process health data change:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Check if leaderboard update notification should be sent
     */
    private async checkAndSendLeaderboardUpdate(
        type: string,
        value: number
    ): Promise<void> {
        try {
            if (!this.currentUser) return;

            // Get active challenges for the user
            const activeChallenges = await this.challengeService.getActiveChallenges(this.currentUser.id);
            if (!activeChallenges || activeChallenges.length === 0) return;

            console.log(`🔧 HealthIntegrationManager: Found ${activeChallenges.length} active challenges`);

            // Process each active challenge
            for (const challenge of activeChallenges) {
                if (this.shouldCheckLeaderboardUpdate(challenge, type, value)) {
                    const notification = await this.notificationScheduler.generateLeaderboardUpdate(
                        this.currentUser.id,
                        challenge.id
                    );

                    if (notification) {
                        // This will be handled by platform-specific code
                        console.log('🔔 HealthIntegrationManager: Leaderboard update notification generated:', notification);
                    }
                }
            }

        } catch (error) {
            console.error('❌ HealthIntegrationManager: Failed to check leaderboard update:', error);
        }
    }

    /**
     * Determine if leaderboard update should be checked
     */
    private shouldCheckLeaderboardUpdate(
        challenge: any,
        type: string,
        value: number
    ): boolean {
        // Only check for challenges that match the health data type
        const metricMapping: Record<string, string[]> = {
            steps: ['steps', 'step_count'],
            distance: ['distance', 'walking_distance', 'running_distance'],
            calories: ['calories', 'energy_burned'],
            activeMinutes: ['time', 'active_minutes', 'exercise_time']
        };

        const relevantMetrics = metricMapping[type] || [];
        return relevantMetrics.includes(challenge.metric);
    }

    /**
     * Manually trigger challenge reminders
     */
    async triggerChallengeReminders(): Promise<ApiResponse<void>> {
        try {
            if (!this.currentUser) {
                throw new Error('No user is currently set');
            }

            console.log('🔔 HealthIntegrationManager: Triggering challenge reminders...');

            const reminders = await this.notificationScheduler.generateChallengeReminder(this.currentUser.id);

            if (reminders.length > 0) {
                console.log(`🔔 HealthIntegrationManager: Generated ${reminders.length} challenge reminders`);
                // Platform-specific code will handle the actual notification display
            } else {
                console.log('🔔 HealthIntegrationManager: No challenge reminders to send');
            }

            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthIntegrationManager: Failed to trigger challenge reminders:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Get current integration status
     */
    getStatus(): IntegrationStatus {
        const watcherStatus = this.healthDataWatcher.getStatus();
        const notificationStats = this.notificationScheduler.getStats();

        return {
            watcherActive: watcherStatus.isActive,
            notificationsActive: this.notificationScheduler.isActive(),
            lastHealthSync: watcherStatus.lastSync,
            lastNotificationSent: notificationStats.lastReminderSent,
            activeChallenges: watcherStatus.activeChallenges,
            errors: watcherStatus.errors
        };
    }

    /**
     * Update configuration
     */
    updateConfig(newConfig: Partial<HealthIntegrationConfig>): void {
        this.config = { ...this.config, ...newConfig };

        // Update watcher configuration
        if (newConfig.watcher) {
            this.healthDataWatcher.updateConfig(newConfig.watcher);
        }

        // Update notification configuration
        if (newConfig.notifications) {
            this.notificationScheduler.updateSchedule(newConfig.notifications);
        }

        console.log('🔧 HealthIntegrationManager: Configuration updated:', this.config);
    }

    /**
     * Get current configuration
     */
    getConfig(): HealthIntegrationConfig {
        return { ...this.config };
    }

    /**
     * Get health data watcher instance
     */
    getHealthDataWatcher(): HealthDataWatcher {
        return this.healthDataWatcher;
    }

    /**
     * Get notification scheduler instance
     */
    getNotificationScheduler(): NotificationScheduler {
        return this.notificationScheduler;
    }

    /**
     * Check if manager is initialized
     */
    isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * Check if services are running
     */
    areServicesRunning(): boolean {
        return this.healthDataWatcher.getStatus().isActive &&
            this.notificationScheduler.isActive();
    }
}
